import { useState } from "react";
import { http, apiError } from "@/lib/api";
import { Prohibit, X, Warning } from "@phosphor-icons/react";

const REASONS = [
  { key: "no_payment",       label: "Non-payment / failed billing" },
  { key: "coach_terminated", label: "Coach removed access" },
  { key: "too_expensive",    label: "Client said too expensive" },
  { key: "not_using_it",     label: "Client not using it enough" },
  { key: "didnt_see_results",label: "Client didn't see results" },
  { key: "life_circumstances",label: "Life got in the way" },
  { key: "found_alternative",label: "Found another program" },
  { key: "tech_issues",      label: "Tech issues" },
  { key: "other",            label: "Other" },
];

/**
 * CoachCancelClientButton — coach-side subscription cancellation.
 * Two modes:
 *   - period_end: cancel_at_period_end=true (client keeps access until sub renews)
 *   - immediate:  cancel Stripe sub NOW and revoke app access instantly (no refund)
 * Falls back gracefully to local-only cancel when the client has no Stripe sub.
 */
export default function CoachCancelClientButton({ clientId, clientName, membership, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("period_end");
  const [reason, setReason] = useState("no_payment");
  const [comment, setComment] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");

  const alreadyCancelled = membership?.status === "canceled" || (!membership?.active && membership?.status !== "coach");
  const firstName = clientName?.split(" ")[0] || "client";
  const confirmPhrase = `CANCEL ${firstName.toUpperCase()}`;
  const canSubmit = !sending && reason && (mode === "period_end" || confirmText.trim() === confirmPhrase);

  const closeModal = () => {
    if (sending) return;
    setOpen(false);
    setErr(""); setStatus(""); setConfirmText("");
  };

  const submit = async () => {
    setErr(""); setStatus(""); setSending(true);
    try {
      const { data } = await http.post(`/coach/clients/${clientId}/cancel-subscription`, {
        mode,
        reason,
        comment: comment.trim() || null,
      });
      const label = data.mode === "immediate" ? "Access revoked now." : "Cancel at period end scheduled.";
      const stripeNote = data.stripe_error ? ` (Stripe: ${data.stripe_error.slice(0, 80)})` : "";
      setStatus(`${label}${stripeNote}`);
      if (typeof onCancelled === "function") {
        try { onCancelled(data); } catch { /* noop */ }
      }
      setTimeout(() => { closeModal(); }, 1600);
    } catch (e) {
      setErr(apiError(e, "Cancellation failed. Try again or check Stripe manually."));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="id-btn-ghost text-xs border border-[#FF3333]/40 text-[#FF9999] hover:bg-[#FF3333]/10"
        data-testid="coach-cancel-client-btn"
        title="Cancel this client's subscription"
      >
        <Prohibit size={14} weight="bold" /> Cancel Sub
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="coach-cancel-modal"
        >
          <div
            className="bg-[#0F0F0F] border border-[#FF3333]/60 max-w-lg w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 border-b border-[#262626]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 border border-[#FF3333]/60 bg-[#FF3333]/10 flex items-center justify-center shrink-0">
                  <Prohibit size={18} weight="fill" className="text-[#FF3333]" />
                </div>
                <div>
                  <div className="id-label text-[#FF3333] mb-0.5">— Cancel Subscription —</div>
                  <div className="font-oswald text-lg text-white uppercase tracking-wide leading-tight">
                    End {firstName}'s membership
                  </div>
                </div>
              </div>
              <button onClick={closeModal} className="text-zinc-500 hover:text-white" data-testid="coach-cancel-close">
                <X size={20} weight="bold" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              {/* Current state pill */}
              <div className="border border-[#262626] px-3 py-2 text-xs font-manrope text-zinc-400" data-testid="coach-cancel-current-state">
                <div className="id-label mb-1">Current status</div>
                <div className="text-white">
                  Status: <span className="uppercase tracking-wide">{membership?.status || "unknown"}</span>
                  {membership?.cancel_at_period_end ? " · cancels at period end" : ""}
                  {membership?.days_remaining != null && membership?.active ? ` · ${membership.days_remaining} days remaining` : ""}
                </div>
                <div className="text-zinc-500 mt-1">
                  Stripe sub: {membership?.has_subscription ? "linked" : "none (local-only cancel)"}
                </div>
                {alreadyCancelled && (
                  <div className="mt-2 text-[#FF9999] flex items-center gap-1.5">
                    <Warning size={12} weight="fill" /> Already cancelled or inactive.
                  </div>
                )}
              </div>

              {/* Mode */}
              <div>
                <div className="id-label mb-2">Cancellation mode</div>
                <div className="grid grid-cols-1 gap-1.5">
                  <label className={`border px-3 py-2 text-xs font-manrope cursor-pointer transition-colors ${mode === "period_end" ? "border-[#FF3333] bg-[#FF3333]/10" : "border-[#262626] hover:border-[#FF3333]/40"}`}>
                    <input
                      type="radio"
                      name="cancel-mode"
                      value="period_end"
                      checked={mode === "period_end"}
                      onChange={() => setMode("period_end")}
                      className="mr-2 accent-[#FF3333]"
                      data-testid="coach-cancel-mode-period-end"
                    />
                    <span className="text-white font-semibold">End of billing period</span>
                    <div className="text-zinc-500 ml-6 mt-0.5">Client keeps access until their sub renews. Standard, no drama.</div>
                  </label>
                  <label className={`border px-3 py-2 text-xs font-manrope cursor-pointer transition-colors ${mode === "immediate" ? "border-[#FF3333] bg-[#FF3333]/10" : "border-[#262626] hover:border-[#FF3333]/40"}`}>
                    <input
                      type="radio"
                      name="cancel-mode"
                      value="immediate"
                      checked={mode === "immediate"}
                      onChange={() => setMode("immediate")}
                      className="mr-2 accent-[#FF3333]"
                      data-testid="coach-cancel-mode-immediate"
                    />
                    <span className="text-white font-semibold">Cancel immediately</span>
                    <div className="text-zinc-500 ml-6 mt-0.5">Revokes app access + cancels Stripe sub now. No refund.</div>
                  </label>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="id-label block mb-1">Reason</label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="id-input text-xs uppercase tracking-[0.1em]"
                  data-testid="coach-cancel-reason"
                >
                  {REASONS.map((r) => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="id-label block mb-1">Internal note (optional)</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={2000}
                  rows={2}
                  className="id-input"
                  placeholder="Context for your records — e.g. 'James asked over DM' or 'card declined 3x'"
                  data-testid="coach-cancel-comment"
                />
              </div>

              {mode === "immediate" && (
                <div className="border border-[#FF3333]/60 bg-[#FF3333]/10 px-3 py-2 text-xs font-manrope space-y-2" data-testid="coach-cancel-confirm-box">
                  <div className="text-[#FF9999] flex items-center gap-1.5">
                    <Warning size={12} weight="fill" /> Immediate cancel is irreversible. Type <span className="text-white font-semibold">{confirmPhrase}</span> to confirm.
                  </div>
                  <input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="id-input"
                    placeholder={confirmPhrase}
                    data-testid="coach-cancel-confirm-input"
                  />
                </div>
              )}

              {err && (
                <div className="border border-[#FF3333]/60 bg-[#FF3333]/10 text-[#FF9999] px-3 py-2 text-xs font-manrope" data-testid="coach-cancel-err">
                  {err}
                </div>
              )}
              {status && (
                <div className="border border-[#FF3333]/40 bg-[#FF3333]/10 text-[#FF9999] px-3 py-2 text-xs font-manrope" data-testid="coach-cancel-status">
                  {status}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[#262626] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={closeModal}
                disabled={sending}
                className="id-btn-ghost text-xs disabled:opacity-40"
                data-testid="coach-cancel-abort"
              >
                Keep membership
              </button>
              <button
                onClick={submit}
                disabled={!canSubmit}
                className="id-btn-primary text-xs disabled:opacity-40 bg-[#FF3333]"
                data-testid="coach-cancel-submit"
              >
                <Prohibit size={14} weight="fill" /> {sending ? "Cancelling…" : (mode === "immediate" ? "Cancel now" : "Cancel at period end")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
